/**
 * **Les doublons exacts, écartés avant l'envoi** *(K2, `doublons.md` §3)*.
 *
 * Tout se décide ici, dans le navigateur : le client calcule le SHA-256 de chaque
 * fichier choisi et le compare à ce qu'il sait déjà de **son** espace — le miroir,
 * la corbeille, et la sélection elle-même. Le serveur ne fait que porter
 * l'empreinte (`Sha256`) ; il ne revérifie rien (DB6).
 *
 * Le module est **pur**, sauf `sha256Hex` : la décision se mesure sans compte, sans
 * site déployé et sans un octet réel.
 */

/**
 * **Au-delà, pas de contrôle exact** *(DB9)*. `crypto.subtle.digest` ne calcule
 * qu'en un bloc, donc le fichier entier en mémoire : une vidéo de 2 Gio ferait
 * tomber l'onglet d'un téléphone. Les plus gros fichiers sont aussi les plus
 * rares ; ils partent sans empreinte, comme avant K1.
 *
 * 256 Mio couvre toutes les photos (la source d'un `MEDIA` photo ne dépasse pas
 * quelques dizaines de Mo) et les vidéos courtes.
 */
export const EXACT_MAX_BYTES = 256 * 1024 * 1024;

/** L'empreinte en hexadécimal minuscule — la forme que le serveur accepte. */
export async function sha256Hex(file: Blob): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
  let hex = '';
  for (const byte of new Uint8Array(digest)) hex += byte.toString(16).padStart(2, '0');
  return hex;
}

/** Où l'original d'un doublon se trouve déjà. */
export type DuplicateOf = 'space' | 'trash' | 'selection';

/** Ce que l'import va faire, fichier par fichier, dans l'ordre de la sélection. */
export interface ImportPlan {
  /** L'empreinte de chaque fichier, ou `undefined` au-delà d'`EXACT_MAX_BYTES`. */
  readonly hashes: readonly (string | undefined)[];
  /** Pour chaque fichier, l'endroit où son original existe déjà — ou rien. */
  readonly duplicateOf: readonly (DuplicateOf | undefined)[];
}

/**
 * Les empreintes connues de l'espace. **Le miroir d'abord** : un fichier présent
 * à la fois dans l'espace et dans la corbeille — un doublon forcé dont une copie a
 * été supprimée — se dit « dans l'espace », où l'utilisateur le retrouve.
 */
export function knownHashes(
  space: readonly { readonly Sha256?: string }[],
  trash: readonly { readonly Sha256?: string }[],
): ReadonlyMap<string, 'space' | 'trash'> {
  const known = new Map<string, 'space' | 'trash'>();
  for (const e of trash) if (e.Sha256) known.set(e.Sha256, 'trash');
  for (const e of space) if (e.Sha256) known.set(e.Sha256, 'space');
  return known;
}

/**
 * **La décision.** Un fichier est un doublon si son empreinte est connue de
 * l'espace, ou si un fichier **précédent** de la même sélection porte la même : le
 * premier part, les suivants sont écartés. Un fichier sans empreinte n'est jamais
 * un doublon — on ne sait rien de lui.
 */
export function planImport(
  hashes: readonly (string | undefined)[],
  known: ReadonlyMap<string, 'space' | 'trash'>,
): ImportPlan {
  const seen = new Set<string>();
  const duplicateOf = hashes.map((hash): DuplicateOf | undefined => {
    if (hash === undefined) return undefined;
    const where = known.get(hash);
    if (where) return where;
    if (seen.has(hash)) return 'selection';
    seen.add(hash);
    return undefined;
  });
  return { hashes, duplicateOf };
}
